import React from 'react';

import ButtonBase from 'core/modules/atoms/Button/Button';
import SelectCustomizableBase from 'core/modules/atoms/SelectCustomizable';
import TooltipCustomBase from 'core/modules/atoms/Tooltip/Tooltip';
import MarkdownBase from 'core/modules/components/Markdown/Markdown';
import { Dialog as DialogBase } from 'ui';
import InputTextBase from 'ui/atoms/InputText/InputText';

type SelectOption = { label: string; value: string };

export const SelectCustomizable = SelectCustomizableBase as unknown as React.ComponentType<{
  /** Forwarded to react-select; reaches custom components via `selectProps`. */
  allSelectedLabel?: string;
  'aria-label'?: string;
  closeMenuOnSelect?: boolean;
  components?: Record<string, React.ComponentType<any>>;
  isDisabled?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  label?: React.ReactNode;
  maxVisibleValues?: number;
  menuPosition?: 'absolute' | 'fixed';
  name?: string;
  onChange: (option: SelectOption | SelectOption[] | null) => void;
  options: SelectOption[];
  placeholder?: React.ReactNode;
  required?: boolean;
  size?: 'default' | 'small';
  value: SelectOption | SelectOption[] | null;
  variant?: 'default';
}>;

export const Button = ButtonBase as unknown as React.ComponentType<{
  ariaLabel?: string;
  className?: string;
  color?:
    | 'primary'
    | 'transparent'
    | 'transparent-primary'
    | 'transparent-light'
    | 'border'
    | 'white'
    | 'grey'
    | 'grey-secondary'
    | 'orange'
    | 'primary-outlined'
    | 'black'
    | 'alpha';
  disabled?: boolean;
  icon?: string;
  iconColor?: 'default' | 'white' | 'primary' | 'gradient' | 'grey' | 'grey-light';
  iconPosition?: 'default' | 'left' | 'right';
  loading?: boolean;
  onClick?: (event?: React.MouseEvent) => void;
  size?: 'default' | 'small' | 'xs' | 'large' | 'medium';
  text?: string;
  to?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'default' | 'inline';
}>;

export const TooltipCustom = TooltipCustomBase as unknown as React.ComponentType<{
  aria?: 'describedby' | 'labelledby';
  arrow?: boolean;
  children: React.ReactElement;
  disabled?: boolean;
  placement?: string;
  title: React.ReactNode;
}>;

export const Dialog = DialogBase as unknown as React.ComponentType<{
  children?: React.ReactNode;
  className?: string;
  isHeaderHidden?: boolean;
  isOpen: boolean;
  onClose?: () => void;
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | 'all-screen';
  title?: React.ReactNode;
}>;

export const Markdown = MarkdownBase as unknown as React.ComponentType<{
  children: string;
  enableDebug?: boolean;
}>;

export const InputText = InputTextBase as unknown as React.ComponentType<{
  'aria-activedescendant'?: string;
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'aria-label'?: string;
  autoComplete?: string;
  background?: 'default' | 'grey';
  className?: string;
  icon?: string;
  iconLeft?: boolean;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  /** Forwarded to the underlying `<input>` ref (the real InputText exposes `referer`). */
  referer?: React.Ref<HTMLInputElement>;
  role?: string;
  value?: string;
}>;
